import { MouseEvent } from 'react'
import { Typography, Card, Stack, Button, useMediaQuery } from '@mui/material'
import { CardItem, TypDescriptionOne } from './style'
import { ViewCounter } from '../../ui-kit/ViewCounter'
import { useNavigate } from 'react-router-dom'
import { routesProfile } from '../../../consts'
import { IconComments } from '../../../icons/IconComments'
import { IconLike } from '../../../icons/IconLike'
import { IconLikeFilled } from '../../../icons/IconLikeFilled'
import { formatNumber } from '../../../common/FormatNumber'
import { PureLightTheme } from '../../../styles/theme'
import type { ISocialOrderListItem } from '../../../api/types/socialOrders'
import { resolveSocialOrderCommentsTotal } from '../../../api/types/socialOrders'
import { useSocialOrderEngagement } from '../../../hooks/useSocialOrderEngagement'
import { SocialOrderCompletionChip } from '../SocialOrderCompletionChip'
import { COLORS } from '../../../styles/variables'
import modalEmitter from '../../ModalComponent/modalEmitter'
import { SocialOrderLikesModalBody } from '../../Modals/SocialOrderLikesModalBody'
import { toast } from 'react-toastify'
import { profileApi } from '../../../api/profile'

type SocialOrderProps = {
  socialOrder: ISocialOrderListItem
}

export const SocialOrderItem = ({ socialOrder }: SocialOrderProps) => {
  const isTablet = useMediaQuery(PureLightTheme.breakpoints.up('sm'))
  const navigate = useNavigate()
  const { data: me } = profileApi.useGetProfileQuery()
  const pathKey = socialOrder.slug || String(socialOrder.id)
  const {
    isAuthor,
    isSubscribed,
    isLiked,
    likesCount,
    toggleSubscribe,
    toggleLike,
    isSubscribeLoading,
    isLikeLoading,
  } = useSocialOrderEngagement({ socialOrder, cacheKey: pathKey })
  const viewsCount = socialOrder.counters.views
  const commentsCount = resolveSocialOrderCommentsTotal(socialOrder.counters.comments)

  const navigateToSocialOrder = () => {
    navigate(`/${routesProfile.SOCIALORDERS.path}/${pathKey}`)
  }

  const handleEdit = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    navigate(`/${routesProfile.SOCIALORDERS.path}/${socialOrder.id}/edit`)
  }

  const handleToggleSubscribe = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    await toggleSubscribe()
  }

  const handleStopNavigation = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleToggleLike = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (isLikeLoading) return
    if (!me?.id) {
      toast.info('Войдите, чтобы ставить лайки')
      return
    }
    await toggleLike()
  }

  const handleOpenLikes = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (likesCount <= 0) return
    modalEmitter.emitOpenModal({
      dialog: {
        title: 'Лайки',
        body: <SocialOrderLikesModalBody socialOrderId={socialOrder.id} />,
        modalToCentrPosition: true,
      },
    })
  }

  const handleOpenComments = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(`/${routesProfile.SOCIALORDERS.path}/${pathKey}`)
  }

  return (
    <Card
      variant="primaryAdaptiveCard"
      sx={CardItem}
      onClick={navigateToSocialOrder}
    >
      <Stack direction="row" alignItems="flex-start" pt={{ xs: 1.5, sm: 0 }} mb={1}>
        <Stack direction="column" gap={0.5} flex={1} minWidth={0}>
          <SocialOrderCompletionChip isCompleted={Boolean(socialOrder.is_completed)} />
          <Typography variant="h3" noWrap>{socialOrder.title}</Typography>
        </Stack>
        {isAuthor ? (
          <Button variant="follow" sx={{ ml: 1, flexShrink: 0 }} onClick={handleEdit}>Редактировать</Button>
        ) : (
          <Button
            variant="follow"
            sx={{ ml: 1, flexShrink: 0 }}
            disabled={isSubscribeLoading}
            onClick={handleToggleSubscribe}
          >
            {isSubscribed ? 'Подписан' : 'Подписаться'}
          </Button>
        )}
      </Stack>

      <Typography variant="body1" sx={TypDescriptionOne}>{socialOrder.description}</Typography>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        pb={{ xs: 1.5, sm: 0 }}
      >
        <Stack direction="row" gap={{ xs: 2, sm: 1 }} alignItems="center">
          <Button
            variant="contained"
            color="secondary"
            onClick={handleToggleLike}
            onPointerDown={handleStopNavigation}
            disabled={isLikeLoading}
            sx={{
              maxHeight: 32,
              minWidth: 44,
              px: likesCount > 0 ? 1.25 : undefined,
              ...(isLiked
                ? {
                    backgroundColor: COLORS.CATSKILL_WHITE,
                    color: COLORS.BLACK,
                    '&:hover': { backgroundColor: COLORS.LINK_WATER },
                    '@media (hover: none)': {
                      '&:hover': { backgroundColor: COLORS.CATSKILL_WHITE },
                    },
                  }
                : {}),
            }}
          >
            <Stack direction="row" alignItems="center" gap={1} sx={{ lineHeight: 1 }}>
              {isLiked ? <IconLikeFilled fontSize="small" /> : <IconLike fontSize="small" />}
              <span
                onClick={handleOpenLikes}
                onPointerDown={handleStopNavigation}
                style={{ userSelect: 'none' }}
              >
                {formatNumber(likesCount)}
              </span>
            </Stack>
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<IconComments />}
            onClick={handleOpenComments}
            onPointerDown={handleStopNavigation}
          >
            {formatNumber(commentsCount)}
            {isTablet ? ' Комментариев' : ''}
          </Button>
        </Stack>
        <ViewCounter count={viewsCount} />
      </Stack>
    </Card>
  )
}
